/**
 * BudgetOS internal calculation test suite.
 * Calls the same pure functions the app uses — never duplicates math.
 *
 * Open via: npm run test:calc  →  http://localhost:5174/test-runner.html
 * Or: ?debug=1 in the app (dev only).
 */

import {
  applyFilters,
  compute503020,
  computeDashboardSummary,
  computeDebtPayoff,
  computeMonthCategorySummary,
  computeMonthlyBreakdown,
  computeNetWorthStats,
  computeSinkingFundStats,
  computeSpendingCalendar,
  formatCurrencyAmount,
  moneyEq,
  pctEq,
} from "../calculations";
import { buildAnnualReportPayload, resolveReportMonth } from "../pdf/buildReportPayload";
import { summaryMoney, tableMoney } from "../pdf/pdfMoney";
import { collectPdfStaticStrings, createPdfTranslator } from "../pdf/pdfCopy";
import { uiEn, uiEs } from "../../i18n/uiKeys";
import {
  LAUNCH_LOCALES,
  UI_LOCALE_TAGS,
  isLaunchLocale,
} from "../../i18n/localeMeta";
import { translations } from "../../i18n/translations";
import {
  calendarStartsOnMonday,
  weekdaysForLocale,
} from "../../i18n/dates";
import { personLabel } from "../../i18n/labels";
import { pdfFontForTemplate } from "../pdf/pdfFonts";
import {
  buildThemeChartColors,
  CHART_ASSETS_GREEN,
  CHART_DEBTS_RED,
  CHART_EXPENSE_CORAL,
  CHART_NET_WORTH,
  toCanvasColor,
} from "../exportUtils";
import type { Transaction } from "../schema";
import {
  buildFullYearTxs,
  DS_DEBTS,
  DS_EMPTY,
  DS_FUNDS,
  DS_LEGACY_BACKUP,
  DS_NEGATIVE_MONTH,
  DS_NW_LOGS,
  DS_SINGLE_MONTH,
  FIXTURE_CATEGORIES,
  FIXTURE_YEAR,
} from "./fixtures";
import { DEFAULT_CATEGORIES, matchesDefaultCategories } from "../defaultCategories";
import { planClearAllData } from "../clearAllPlan";

export interface AssertionResult {
  suite: string;
  name: string;
  passed: boolean;
  expected: unknown;
  actual: unknown;
}

export interface SuiteReport {
  passed: boolean;
  total: number;
  passedCount: number;
  failedCount: number;
  results: AssertionResult[];
  /** Customer-facing one-liner (no jargon) */
  customerMessage: string;
}

function assert(
  results: AssertionResult[],
  suite: string,
  name: string,
  passed: boolean,
  expected: unknown,
  actual: unknown
) {
  results.push({ suite, name, passed, expected, actual });
}

function assertMoney(
  results: AssertionResult[],
  suite: string,
  name: string,
  expected: number,
  actual: number
) {
  assert(results, suite, name, moneyEq(expected, actual), expected, actual);
}

function assertPct(
  results: AssertionResult[],
  suite: string,
  name: string,
  expected: number,
  actual: number
) {
  assert(results, suite, name, pctEq(expected, actual), expected, actual);
}

// ── Dataset: empty ────────────────────────────────────
function testEmpty(results: AssertionResult[]) {
  const s = "empty";
  const dash = computeDashboardSummary(DS_EMPTY.txs, DS_EMPTY.categories);
  assertMoney(results, s, "Dashboard income = 0", 0, dash.income);
  assertMoney(results, s, "Dashboard expense = 0", 0, dash.expense);
  assertMoney(results, s, "Dashboard leftover = 0", 0, dash.leftover);
  assertPct(results, s, "Dashboard savings rate = 0", 0, dash.savingsRate);
  assertMoney(results, s, "Total saved = 0", 0, dash.actualSaved);

  const cal = computeSpendingCalendar([], FIXTURE_YEAR, 5, 15);
  assertMoney(results, s, "Calendar total spend = 0", 0, cal.totalSpend);
  assert(results, s, "Calendar no-spend days = 15", cal.noSpendDays === 15, 15, cal.noSpendDays);

  const nw = computeNetWorthStats([]);
  assertMoney(results, s, "Net worth = 0", 0, nw.netWorth);
  assert(results, s, "MoM delta = null", nw.momDelta === null, null, nw.momDelta);

  assert(results, s, "Debt payoff empty", computeDebtPayoff([]).length === 0, 0, computeDebtPayoff([]).length);
}

// ── Dataset: single month ─────────────────────────────
function testSingleMonth(results: AssertionResult[]) {
  const s = "single-month";
  const cats = FIXTURE_CATEGORIES;
  const txs = DS_SINGLE_MONTH;
  const year = FIXTURE_YEAR;
  const month = 5;

  const dash = computeDashboardSummary(txs, cats);
  assertMoney(results, s, "Money In = 5000", 5000, dash.income);
  assertMoney(results, s, "Money Out = 2000", 2000, dash.expense);
  assertMoney(results, s, "Leftover = 3000", 3000, dash.leftover);
  assertPct(results, s, "Savings rate = 60%", 60, dash.savingsRate);
  assertMoney(results, s, "Total Saved = 300", 300, dash.actualSaved);

  const budget = computeMonthCategorySummary(txs, cats, year, month);
  const incomeTotal = budget.income.reduce((a, c) => a + c.actual, 0);
  const expenseTotal = budget.expense.reduce((a, c) => a + c.actual, 0);
  assertMoney(results, s, "Monthly Budget income total = 5000", 5000, incomeTotal);
  assertMoney(results, s, "Monthly Budget expense total = 2000", 2000, expenseTotal);

  const rent = budget.expense.find((c) => c.id === "cat-rent")!;
  assertMoney(results, s, "Rent actual = 1500", 1500, rent.actual);
  assertPct(results, s, "Rent % of budget = 100%", 100, rent.pct);

  const needsGroup = budget.expense
    .filter((c) => c.group === "needs")
    .reduce((a, c) => a + c.actual, 0);
  const wantsGroup = budget.expense
    .filter((c) => c.group === "wants")
    .reduce((a, c) => a + c.actual, 0);
  const savingsGroup = budget.expense
    .filter((c) => c.group === "savings")
    .reduce((a, c) => a + c.actual, 0);
  assertMoney(results, s, "Needs group = 1500", 1500, needsGroup);
  assertMoney(results, s, "Wants group = 200", 200, wantsGroup);
  assertMoney(results, s, "Savings group = 300", 300, savingsGroup);

  const rule = compute503020(txs, cats, year, month);
  assertPct(results, s, "50/30/20 Needs % = 30", 30, rule.needsPct);
  assertPct(results, s, "50/30/20 Wants % = 4", 4, rule.wantsPct);
  assertPct(results, s, "50/30/20 Savings % = 6", 6, rule.savingsPct);
  assert(results, s, "Needs under target (30 < 50)", rule.needsPct < rule.needsTarget, true, rule.needsPct < 50);
  assert(results, s, "Wants under target (4 < 30)", rule.wantsPct < rule.wantsTarget, true, rule.wantsPct < 30);
  assert(results, s, "Savings under target (6 < 20)", rule.savingsPct < rule.savingsTarget, true, rule.savingsPct < 20);

  // Calendar as-of day 20: spend on 1,15,20 → days 1–20
  const cal = computeSpendingCalendar(txs, year, month, 20);
  assertMoney(results, s, "Calendar day 1 = 1500", 1500, cal.byDay[1] ?? 0);
  assertMoney(results, s, "Calendar day 15 = 200", 200, cal.byDay[15] ?? 0);
  assertMoney(results, s, "Calendar day 20 = 300", 300, cal.byDay[20] ?? 0);
  assertMoney(results, s, "Calendar monthly total = 2000", 2000, cal.totalSpend);
  assert(results, s, "Calendar spend days = 3", cal.spendDays === 3, 3, cal.spendDays);
  assert(results, s, "Calendar no-spend days = 17", cal.noSpendDays === 17, 17, cal.noSpendDays);
  assertMoney(results, s, "Calendar avg/day ≈ 666.67", 2000 / 3, cal.avgPerDay);
  assert(results, s, "Calendar streak from day 20 = 0 (spent)", cal.streak === 0, 0, cal.streak);

  const calEnd = computeSpendingCalendar(txs, year, month, 22);
  assert(results, s, "Calendar streak from day 22 = 2", calEnd.streak === 2, 2, calEnd.streak);
}

// ── Dataset: negative month ───────────────────────────
function testNegativeMonth(results: AssertionResult[]) {
  const s = "negative-month";
  const dash = computeDashboardSummary(DS_NEGATIVE_MONTH, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Income = 1000", 1000, dash.income);
  assertMoney(results, s, "Expense = 2500", 2500, dash.expense);
  assertMoney(results, s, "Leftover = -1500", -1500, dash.leftover);
  assertPct(results, s, "Savings rate = -150%", -150, dash.savingsRate);

  const months = computeMonthlyBreakdown(DS_NEGATIVE_MONTH, FIXTURE_YEAR);
  assertMoney(results, s, "June leftover = -1500", -1500, months[5].leftover);
}

// ── Dataset: full year ────────────────────────────────
function testFullYear(results: AssertionResult[]) {
  const s = "full-year";
  const txs = buildFullYearTxs();
  const dash = computeDashboardSummary(txs, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Year Money In = 66000", 66000, dash.income);
  assertMoney(results, s, "Year Money Out = 28800", 28800, dash.expense);
  assertMoney(results, s, "Year Leftover = 37200", 37200, dash.leftover);
  assertPct(results, s, "Year Savings Rate ≈ 56.3636%", (37200 / 66000) * 100, dash.savingsRate);
  assertMoney(results, s, "Year Total Saved = 3600", 3600, dash.actualSaved);
  assertMoney(results, s, "Year Saved Target = 3600", 3600, dash.totalSavedTarget);

  const months = computeMonthlyBreakdown(txs, FIXTURE_YEAR);
  for (let i = 0; i < 12; i++) {
    assertMoney(results, s, `Month ${i + 1} income = 5500`, 5500, months[i].totalIncome);
    assertMoney(results, s, `Month ${i + 1} expense = 2400`, 2400, months[i].totalExpense);
  }

  const rule = compute503020(txs, FIXTURE_CATEGORIES, FIXTURE_YEAR, 3);
  // needs 1900, wants 200, savings 300 of 5500
  assertMoney(results, s, "Mar Needs = 1900", 1900, rule.needs);
  assertPct(results, s, "Mar Needs % ≈ 34.545%", (1900 / 5500) * 100, rule.needsPct);
  assertPct(results, s, "Mar Wants % ≈ 3.636%", (200 / 5500) * 100, rule.wantsPct);
  assertPct(results, s, "Mar Savings % ≈ 5.455%", (300 / 5500) * 100, rule.savingsPct);
}

// ── Debt payoff ───────────────────────────────────────
function testDebts(results: AssertionResult[]) {
  const s = "debts";
  const start = new Date(2026, 0, 1); // Jan 1 2026

  const snowball = computeDebtPayoff(DS_DEBTS, "snowball", start);
  assert(results, s, "Snowball order: Card then Loan", snowball[0].debt.id === "d1" && snowball[1].debt.id === "d2", "d1,d2", snowball.map((r) => r.debt.id).join(","));

  // Card: payment 150, 12% APR → month 1 interest = 10, principal = 140, bal = 860
  const cardM1 = snowball[0].schedule[0];
  assertMoney(results, s, "Card M1 interest = 10", 10, cardM1.interest);
  assertMoney(results, s, "Card M1 principal = 140", 140, cardM1.principal);
  assertMoney(results, s, "Card M1 balance = 860", 860, cardM1.balance);
  assertMoney(results, s, "Card M1 payment = 150", 150, cardM1.payment);

  // Card pays off in 7 months; total interest ≈ 40.11
  assert(results, s, "Card months to payoff = 7", snowball[0].monthsToPayoff === 7, 7, snowball[0].monthsToPayoff);
  assertMoney(results, s, "Card total interest ≈ 40.11", 40.11, snowball[0].totalInterest);
  assert(results, s, "Card final balance = 0", moneyEq(snowball[0].schedule.at(-1)!.balance, 0), 0, snowball[0].schedule.at(-1)!.balance);

  // Loan gets cascadeExtra after card paid: minPayment of card (100) added after card months in cascade model
  // Avalanche should put Loan first (higher rate? Card is 12%, Loan 6% → Card first for avalanche too)
  const avalanche = computeDebtPayoff(DS_DEBTS, "avalanche", start);
  assert(results, s, "Avalanche order: Card (12%) then Loan (6%)", avalanche[0].debt.id === "d1", "d1", avalanche[0].debt.id);

  const totalDebt = DS_DEBTS.reduce((a, d) => a + d.balance, 0);
  assertMoney(results, s, "Total debt balance = 6000", 6000, totalDebt);

  const totalInterestSnow = snowball.reduce((a, r) => a + r.totalInterest, 0);
  const totalInterestAval = avalanche.reduce((a, r) => a + r.totalInterest, 0);
  assert(results, s, "Avalanche interest ≤ snowball interest (same order here → equal)", moneyEq(totalInterestAval, totalInterestSnow), totalInterestSnow, totalInterestAval);

  // Mid-year paid-off debt: balance that clears in 1 payment
  const paidMid: typeof DS_DEBTS = [
    { id: "tiny", name: "Tiny", balance: 50, interestRate: 0, minPayment: 50, extraPayment: 0 },
    { id: "big", name: "Big", balance: 2000, interestRate: 10, minPayment: 100, extraPayment: 0 },
  ];
  const mid = computeDebtPayoff(paidMid, "snowball", start);
  assert(results, s, "Tiny debt pays in 1 month", mid[0].monthsToPayoff === 1, 1, mid[0].monthsToPayoff);
  assertMoney(results, s, "Tiny interest = 0", 0, mid[0].totalInterest);
  // After tiny paid, cascade adds 50 to big's payment
  assert(results, s, "Big gets cascade (faster than alone)", mid[1].monthsToPayoff < computeDebtPayoff([paidMid[1]], "snowball", start)[0].monthsToPayoff, true, mid[1].monthsToPayoff);
}

// ── Sinking funds ─────────────────────────────────────
function testSinkingFunds(results: AssertionResult[]) {
  const s = "sinking-funds";
  const asOf = new Date(2026, 0, 1); // Jan 2026 → July = 6 months

  const zero = computeSinkingFundStats(DS_FUNDS[0], asOf);
  assertPct(results, s, "Zero fund pct = 0%", 0, zero.pct);
  assertMoney(results, s, "Zero remaining = 1000", 1000, zero.remaining);
  assert(results, s, "Zero months left = 6", zero.monthsLeft === 6, 6, zero.monthsLeft);
  assertMoney(results, s, "Zero needed/month ≈ 166.67", 1000 / 6, zero.monthlyNeeded);

  const half = computeSinkingFundStats(DS_FUNDS[1], asOf);
  assertPct(results, s, "Half fund pct = 50%", 50, half.pct);
  assertMoney(results, s, "Half remaining = 1000", 1000, half.remaining);

  const full = computeSinkingFundStats(DS_FUNDS[2], asOf);
  assertPct(results, s, "Full fund pct = 100%", 100, full.pct);
  assertMoney(results, s, "Full remaining = 0", 0, full.remaining);
  assertMoney(results, s, "Full needed/month = 0", 0, full.monthlyNeeded);

  const over = computeSinkingFundStats(DS_FUNDS[3], asOf);
  assertPct(results, s, "Over fund pct = 150%", 150, over.pct);
  assert(results, s, "Overfunded flag true", over.overfunded === true, true, over.overfunded);
  assertMoney(results, s, "Over remaining = -250", -250, over.remaining);
}

// ── Net worth ─────────────────────────────────────────
function testNetWorth(results: AssertionResult[]) {
  const s = "net-worth";
  const nw = computeNetWorthStats(DS_NW_LOGS);
  assertMoney(results, s, "Assets = 105000", 105000, nw.assets);
  assertMoney(results, s, "Debts = 38000", 38000, nw.debts);
  assertMoney(results, s, "Net worth = 67000", 67000, nw.netWorth);
  assertPct(results, s, "Debt ratio ≈ 36.190%", (38000 / 105000) * 100, nw.debtRatio);
  // Jan NW = 60000, Feb = 67000 → delta 7000
  assertMoney(results, s, "MoM delta = 7000", 7000, nw.momDelta ?? 0);
}

// ── Currency / locale formatting (raw unchanged) ──────
function testCurrencyLocale(results: AssertionResult[]) {
  const s = "currency-locale";
  const amount = 1234.56;
  const usd = formatCurrencyAmount(amount, "USD", "en-US");
  const eur = formatCurrencyAmount(amount, "EUR", "de-DE");
  const gbp = formatCurrencyAmount(amount, "GBP", "en-GB");

  assert(results, s, "USD format contains 1,234.56", usd.includes("1,234.56"), true, usd);
  assert(results, s, "EUR format differs from USD string", eur !== usd, true, `${eur} vs ${usd}`);
  assert(results, s, "GBP format differs from USD string", gbp !== usd, true, `${gbp} vs ${usd}`);

  // Same dataset totals regardless of display currency
  const dash = computeDashboardSummary(DS_SINGLE_MONTH, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Raw income unchanged under locale switch", 5000, dash.income);
  assertMoney(results, s, "Raw expense unchanged under locale switch", 2000, dash.expense);
}

// ── Lifecycle: add / edit / delete / import ────────────
function testLifecycle(results: AssertionResult[]) {
  const s = "lifecycle";
  let txs: Transaction[] = [...DS_SINGLE_MONTH];
  const cats = FIXTURE_CATEGORIES;

  // Add $100 groceries expense on May 15
  const added: Transaction = {
    id: "life-add",
    date: "2026-05-15",
    type: "expense",
    amount: 100,
    categoryId: "cat-groc",
    accountId: "a1",
    person: "Household",
  };
  txs = [...txs, added];

  let dash = computeDashboardSummary(txs, cats);
  assertMoney(results, s, "After add: expense = 2100", 2100, dash.expense);
  assertMoney(results, s, "After add: leftover = 2900", 2900, dash.leftover);

  let budget = computeMonthCategorySummary(txs, cats, 2026, 5);
  assertMoney(results, s, "After add: groceries = 100", 100, budget.expense.find((c) => c.id === "cat-groc")!.actual);

  let cal = computeSpendingCalendar(txs, 2026, 5, 20);
  assertMoney(results, s, "After add: day 15 = 300", 300, cal.byDay[15] ?? 0);

  let rule = compute503020(txs, cats, 2026, 5);
  assertMoney(results, s, "After add: needs = 1600", 1600, rule.needs);
  assertPct(results, s, "After add: needs % = 32", 32, rule.needsPct);

  // Edit: change amount to 250 and move to June
  txs = txs.map((t) =>
    t.id === "life-add" ? { ...t, amount: 250, date: "2026-06-15" } : t
  );
  dash = computeDashboardSummary(txs, cats);
  assertMoney(results, s, "After edit: year expense = 2250", 2250, dash.expense);

  budget = computeMonthCategorySummary(txs, cats, 2026, 5);
  assertMoney(results, s, "After move: May groceries = 0", 0, budget.expense.find((c) => c.id === "cat-groc")!.actual);

  const juneBudget = computeMonthCategorySummary(txs, cats, 2026, 6);
  assertMoney(results, s, "After move: June groceries = 250", 250, juneBudget.expense.find((c) => c.id === "cat-groc")!.actual);

  cal = computeSpendingCalendar(txs, 2026, 5, 20);
  assertMoney(results, s, "After move: May day 15 back to 200", 200, cal.byDay[15] ?? 0);

  // Delete
  txs = txs.filter((t) => t.id !== "life-add");
  dash = computeDashboardSummary(txs, cats);
  assertMoney(results, s, "After delete: expense back to 2000", 2000, dash.expense);
  assertMoney(results, s, "After delete: leftover back to 3000", 3000, dash.leftover);

  // Import legacy backup → totals match pre-export shape
  const imported = DS_LEGACY_BACKUP.tables.transactions as Transaction[];
  const importedDash = computeDashboardSummary(imported, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Import legacy: income = 5000", 5000, importedDash.income);
  assertMoney(results, s, "Import legacy: expense = 1500", 1500, importedDash.expense);
  assertMoney(results, s, "Import legacy: leftover = 3500", 3500, importedDash.leftover);

  // Schema migration parity: v1 rows without tags/notes still compute identically
  const migrated = imported.map((t) => ({ ...t, tags: [], notes: "" }));
  const migratedDash = computeDashboardSummary(migrated, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Post-migration income matches", importedDash.income, migratedDash.income);
  assertMoney(results, s, "Post-migration expense matches", importedDash.expense, migratedDash.expense);
}

// ── CSV / export number parity (serialize from computed) ─
function testExportParity(results: AssertionResult[]) {
  const s = "export-parity";
  const dash = computeDashboardSummary(DS_SINGLE_MONTH, FIXTURE_CATEGORIES);
  const csvLine = `Money In,${dash.income}\nMoney Out,${dash.expense}\nLeftover,${dash.leftover}`;
  assert(results, s, "CSV contains exact Money In", csvLine.includes(`Money In,${dash.income}`), true, csvLine);
  assert(results, s, "CSV Money Out matches screen", csvLine.includes(`Money Out,${dash.expense}`), true, csvLine);

  const pdfPayload = {
    income: dash.income,
    expense: dash.expense,
    leftover: dash.leftover,
    savingsRate: dash.savingsRate,
  };
  assertMoney(results, s, "PDF payload income = screen", dash.income, pdfPayload.income);
  assertMoney(results, s, "PDF payload expense = screen", dash.expense, pdfPayload.expense);
  assertPct(results, s, "PDF payload savings rate = screen", dash.savingsRate, pdfPayload.savingsRate);
}

// ── Purpose-built PDF report payload vs on-screen calcs ─
function testPdfReportPayload(results: AssertionResult[]) {
  const s = "pdf-report";
  const year = FIXTURE_YEAR;
  const txs = buildFullYearTxs();
  const asOf = new Date("2026-01-01T12:00:00");

  const screenDash = computeDashboardSummary(
    applyFilters(txs, { year, person: "All" }),
    FIXTURE_CATEGORIES
  );
  const screenMonthly = computeMonthlyBreakdown(applyFilters(txs, { year }), year);
  const screenRule = compute503020(applyFilters(txs, { year }), FIXTURE_CATEGORIES, year, 5);
  const screenBudget = computeMonthCategorySummary(
    applyFilters(txs, { year }),
    FIXTURE_CATEGORIES,
    year,
    5
  );
  const screenDebts = computeDebtPayoff(DS_DEBTS, "snowball", asOf);
  const screenNw = computeNetWorthStats(DS_NW_LOGS);

  const report = buildAnnualReportPayload({
    year,
    month: 5,
    person: "All",
    transactions: txs,
    categories: FIXTURE_CATEGORIES,
    debts: DS_DEBTS,
    sinkingFunds: DS_FUNDS,
    netWorthLogs: DS_NW_LOGS,
    accounts: [],
    currency: "USD",
    locale: "en-US",
    generatedAt: asOf,
    debtMethod: "snowball",
  });

  // Dashboard KPIs
  assertMoney(results, s, "PDF dash income = screen", screenDash.income, report.assertionNumbers["dashboard.income"]);
  assertMoney(results, s, "PDF dash expense = screen", screenDash.expense, report.assertionNumbers["dashboard.expense"]);
  assertMoney(results, s, "PDF dash leftover = screen", screenDash.leftover, report.assertionNumbers["dashboard.leftover"]);
  assertPct(results, s, "PDF dash savingsRate = screen", screenDash.savingsRate, report.assertionNumbers["dashboard.savingsRate"]);
  assertMoney(results, s, "PDF dash actualSaved = screen", screenDash.actualSaved, report.assertionNumbers["dashboard.actualSaved"]);
  assertMoney(results, s, "PDF KPI moneyIn = dash", screenDash.income, report.kpis.find((k) => k.key === "moneyIn")!.value);

  // Cash flow months
  screenMonthly.forEach((m) => {
    assertMoney(
      results,
      s,
      `PDF cashflow ${m.month} income`,
      m.totalIncome,
      report.assertionNumbers[`cashflow.${m.month}.income`]
    );
    assertMoney(
      results,
      s,
      `PDF cashflow ${m.month} expense`,
      m.totalExpense,
      report.assertionNumbers[`cashflow.${m.month}.expense`]
    );
  });

  // 50/30/20 for May
  assertMoney(results, s, "PDF rule needs = screen", screenRule.needs, report.assertionNumbers["rule.needs"]);
  assertMoney(results, s, "PDF rule wants = screen", screenRule.wants, report.assertionNumbers["rule.wants"]);
  assertMoney(results, s, "PDF rule savings = screen", screenRule.savings, report.assertionNumbers["rule.savings"]);
  assertPct(results, s, "PDF rule needsPct = screen", screenRule.needsPct, report.assertionNumbers["rule.needsPct"]);

  // Budget category actuals
  screenBudget.expense.forEach((c) => {
    assertMoney(
      results,
      s,
      `PDF budget ${c.id} actual`,
      c.actual,
      report.assertionNumbers[`budget.expense.${c.id}.actual`]
    );
  });

  // Debts
  const totalBal = DS_DEBTS.reduce((a, d) => a + d.balance, 0);
  const totalInt = screenDebts.reduce((a, r) => a + r.totalInterest, 0);
  assertMoney(results, s, "PDF debts totalBalance", totalBal, report.assertionNumbers["debts.totalBalance"]);
  assertMoney(results, s, "PDF debts totalInterest", totalInt, report.assertionNumbers["debts.totalInterest"]);
  screenDebts.forEach((r) => {
    assertMoney(
      results,
      s,
      `PDF debt ${r.debt.id} interest`,
      r.totalInterest,
      report.assertionNumbers[`debt.${r.debt.id}.totalInterest`]
    );
  });

  // Net worth
  assertMoney(results, s, "PDF NW net = screen", screenNw.netWorth, report.assertionNumbers["netWorth.net"]);
  assertMoney(results, s, "PDF NW assets = screen", screenNw.assets, report.assertionNumbers["netWorth.assets"]);

  // Funds
  DS_FUNDS.forEach((f) => {
    const stats = computeSinkingFundStats(f, asOf);
    assertMoney(results, s, `PDF fund ${f.id} pct`, stats.pct, report.assertionNumbers[`fund.${f.id}.pct`]);
    assertMoney(results, s, `PDF fund ${f.id} current`, f.currentSaved, report.assertionNumbers[`fund.${f.id}.current`]);
  });

  // Display formatting uses full precision (same as table cells)
  const display = formatCurrencyAmount(screenDash.income, "USD", "en-US", false);
  assert(results, s, "PDF KPI display uses formatCurrencyAmount", report.kpis[0].display === display, display, report.kpis[0].display);

  // ── Auto-resolved period (annual report without explicit month) ──
  // Full-year fixture → latest month with data = December
  const autoMonth = resolveReportMonth(year, applyFilters(txs, { year }), undefined, asOf);
  assert(results, s, "resolveReportMonth → Dec for full-year data", autoMonth === 12, 12, autoMonth);

  const autoReport = buildAnnualReportPayload({
    year,
    // month omitted on purpose
    person: "All",
    transactions: txs,
    categories: FIXTURE_CATEGORIES,
    debts: DS_DEBTS,
    sinkingFunds: DS_FUNDS,
    netWorthLogs: DS_NW_LOGS,
    currency: "USD",
    locale: "en-US",
    generatedAt: asOf,
  });
  assert(results, s, "Auto annual report.month = 12", autoReport.month === 12, 12, autoReport.month);
  assert(results, s, "Auto report.month in assertionNumbers", autoReport.assertionNumbers["report.month"] === 12, 12, autoReport.assertionNumbers["report.month"]);

  const screenDecBudget = computeMonthCategorySummary(
    applyFilters(txs, { year }),
    FIXTURE_CATEGORIES,
    year,
    12
  );
  const screenDecRule = compute503020(applyFilters(txs, { year }), FIXTURE_CATEGORIES, year, 12);

  // Budget actuals must be non-zero and match on-screen for the resolved month
  const rentActual = screenDecBudget.expense.find((c) => c.id === "cat-rent")!.actual;
  assertMoney(results, s, "Auto PDF Dec rent actual > 0", 1500, rentActual);
  assertMoney(
    results,
    s,
    "Auto PDF Dec rent actual = screen",
    rentActual,
    autoReport.assertionNumbers["budget.expense.cat-rent.actual"]
  );
  assertMoney(
    results,
    s,
    "Auto PDF Dec groceries actual = screen",
    screenDecBudget.expense.find((c) => c.id === "cat-groc")!.actual,
    autoReport.assertionNumbers["budget.expense.cat-groc.actual"]
  );

  assertMoney(results, s, "Auto PDF Dec 50/30 needs = screen", screenDecRule.needs, autoReport.assertionNumbers["rule.needs"]);
  assertMoney(results, s, "Auto PDF Dec 50/30 wants = screen", screenDecRule.wants, autoReport.assertionNumbers["rule.wants"]);
  assertMoney(results, s, "Auto PDF Dec 50/30 savings = screen", screenDecRule.savings, autoReport.assertionNumbers["rule.savings"]);
  assertPct(results, s, "Auto PDF Dec needsPct = screen", screenDecRule.needsPct, autoReport.assertionNumbers["rule.needsPct"]);
  assertPct(results, s, "Auto PDF Dec wantsPct = screen", screenDecRule.wantsPct, autoReport.assertionNumbers["rule.wantsPct"]);
  assertPct(results, s, "Auto PDF Dec savingsPct = screen", screenDecRule.savingsPct, autoReport.assertionNumbers["rule.savingsPct"]);
  assert(results, s, "Auto PDF 50/30 needs not zero", autoReport.rule503020.needs > 0, true, autoReport.rule503020.needs);

  // Partial-year activity → resolve to last month with txs (not blank Dec)
  const mayOnly = txs.filter((t) => t.date.startsWith("2026-05"));
  assert(
    results,
    s,
    "resolveReportMonth May-only → 5",
    resolveReportMonth(year, mayOnly) === 5,
    5,
    resolveReportMonth(year, mayOnly)
  );

  // Formatting rule: summary and table both use full 2-decimal precision
  const sample = 22100;
  const interestSample = 201.7;
  assert(
    results,
    s,
    "summaryMoney is full precision",
    summaryMoney(sample, "USD", "en-US") === formatCurrencyAmount(sample, "USD", "en-US", false),
    formatCurrencyAmount(sample, "USD", "en-US", false),
    summaryMoney(sample, "USD", "en-US")
  );
  assert(
    results,
    s,
    "tableMoney matches summaryMoney (full precision)",
    tableMoney(sample, "USD", "en-US") === summaryMoney(sample, "USD", "en-US"),
    summaryMoney(sample, "USD", "en-US"),
    tableMoney(sample, "USD", "en-US")
  );
  assert(
    results,
    s,
    "tableMoney interest uses 2 decimals (not $201.7)",
    tableMoney(interestSample, "USD", "en-US") === formatCurrencyAmount(interestSample, "USD", "en-US", false),
    formatCurrencyAmount(interestSample, "USD", "en-US", false),
    tableMoney(interestSample, "USD", "en-US")
  );
  assert(
    results,
    s,
    "tableMoney interest is not compact K",
    !/\$\d+(\.\d)?K/.test(tableMoney(interestSample, "USD", "en-US")),
    "no K suffix",
    tableMoney(interestSample, "USD", "en-US")
  );

  // ── PDF i18n: Spanish locale must not emit English static chrome ──
  const esT = createPdfTranslator("es");
  const enT = createPdfTranslator("en");
  const mustLocalize = [
    "pdf.sub.incomeSources",
    "pdf.col.category",
    "pdf.col.target",
    "pdf.col.actual",
    "pdf.col.variance",
    "pdf.section.debts",
    "pdf.section.funds",
    "pdf.section.cashFlow",
    "pdf.section.yearGlance",
    "pdf.kpi.moneyIn",
  ] as const;
  for (const key of mustLocalize) {
    const en = enT(key);
    const es = esT(key);
    assert(
      results,
      s,
      `PDF i18n ${key} differs in es`,
      es !== en && es.length > 0,
      en,
      es
    );
  }
  // Representative English phrases that must not appear in Spanish static copy
  const bannedEn = [
    "Income sources",
    "Debt Payoff Summary",
    "Sinking Funds",
    "Year at a Glance",
    "Monthly Cash Flow",
    "Expense categories",
  ];
  const esStatic = collectPdfStaticStrings(esT).join("\n");
  for (const phrase of bannedEn) {
    assert(
      results,
      s,
      `PDF es copy has no English “${phrase}”`,
      !esStatic.includes(phrase),
      "absent",
      esStatic.includes(phrase) ? "present" : "absent"
    );
  }
  assert(
    results,
    s,
    "PDF es has Fuentes de ingresos",
    esStatic.includes(esT("pdf.sub.incomeSources")),
    esT("pdf.sub.incomeSources"),
    esStatic.includes(esT("pdf.sub.incomeSources")) ? "ok" : "missing"
  );

  // ── Chart brand colors (must stay canvas-safe + match in-app palette) ──
  assert(
    results,
    s,
    "toCanvasColor converts space-separated HSL",
    toCanvasColor("hsl(32 70% 52%)") === "hsl(32, 70%, 52%)",
    "hsl(32, 70%, 52%)",
    toCanvasColor("hsl(32 70% 52%)")
  );
  assert(
    results,
    s,
    "toCanvasColor leaves hex untouched",
    toCanvasColor("#ef4444") === "#ef4444",
    "#ef4444",
    toCanvasColor("#ef4444")
  );
  // Sand accent (mustard) — same source as in-app Income / Net Worth primary
  const sandPalette = buildThemeChartColors((name) =>
    name === "--primary" ? "32 70% 52%" : name === "--foreground" ? "222 47% 11%" : name === "--muted-foreground" ? "215 16% 47%" : name === "--border" ? "214 32% 91%" : ""
  );
  assert(
    results,
    s,
    "PDF chart income = theme primary (sand mustard)",
    sandPalette.income === "hsl(32, 70%, 52%)",
    "hsl(32, 70%, 52%)",
    sandPalette.income
  );
  assert(
    results,
    s,
    "PDF chart expense = in-app coral (not blue/orange default)",
    sandPalette.expense === CHART_EXPENSE_CORAL,
    CHART_EXPENSE_CORAL,
    sandPalette.expense
  );
  assert(
    results,
    s,
    "PDF chart debts = NetWorth #ef4444",
    sandPalette.destructive === CHART_DEBTS_RED,
    CHART_DEBTS_RED,
    sandPalette.destructive
  );
  assert(
    results,
    s,
    "PDF chart assets = NetWorth #22c55e",
    sandPalette.assets === CHART_ASSETS_GREEN,
    CHART_ASSETS_GREEN,
    sandPalette.assets
  );
  // Lemon accent (yellow) — sidebar-adjacent mustard/yellow family
  const lemonPalette = buildThemeChartColors((name) =>
    name === "--primary" ? "48 96% 50%" : ""
  );
  assert(
    results,
    s,
    "PDF chart follows lemon primary (not hardcoded blue)",
    lemonPalette.primary === "hsl(48, 96%, 50%)" &&
      !lemonPalette.primary.includes("221") &&
      !lemonPalette.income.includes("221"),
    "hsl(48, 96%, 50%)",
    lemonPalette.primary
  );
  // Embedded PDF font maps to active theme template (not Helvetica)
  assert(
    results,
    s,
    "PDF font clean → NunitoSans",
    pdfFontForTemplate("clean") === "NunitoSans",
    "NunitoSans",
    pdfFontForTemplate("clean")
  );
  assert(
    results,
    s,
    "PDF font fintech → Manrope",
    pdfFontForTemplate("fintech") === "Manrope",
    "Manrope",
    pdfFontForTemplate("fintech")
  );
  assert(
    results,
    s,
    "PDF font forest → PlusJakartaSans",
    pdfFontForTemplate("forest") === "PlusJakartaSans",
    "PlusJakartaSans",
    pdfFontForTemplate("forest")
  );
  assert(
    results,
    s,
    "PDF Net Worth color is theme-independent slate",
    sandPalette.netWorth === CHART_NET_WORTH && sandPalette.netWorth !== sandPalette.primary,
    CHART_NET_WORTH,
    sandPalette.netWorth
  );

  // Locale formatting: same raw amount, different display
  const amt = 1234.56;
  const enMoney = formatCurrencyAmount(amt, "USD", "en-US", false);
  const deMoney = formatCurrencyAmount(amt, "EUR", "de-DE", false);
  assert(
    results,
    s,
    "en-US currency uses $ and period decimals",
    enMoney.includes("$") && enMoney.includes("."),
    "$1,234.56-ish",
    enMoney
  );
  assert(
    results,
    s,
    "de-DE currency uses different separators than en-US",
    deMoney !== enMoney && (deMoney.includes(",") || deMoney.includes("€")),
    "locale-specific",
    deMoney
  );
}

/** §14 — Launch locales: dicts, currency/date/calendar, person presets */
function testLaunchLocales(results: AssertionResult[]) {
  const s = "launch-locales";
  const amt = 1234.56;
  const sampleDate = new Date(2026, 0, 15); // Jan 15, 2026

  assert(
    results,
    s,
    "Exactly 6 launch locales",
    LAUNCH_LOCALES.length === 6 &&
      LAUNCH_LOCALES.every(isLaunchLocale) &&
      !isLaunchLocale("ja") &&
      !isLaunchLocale("zh"),
    6,
    LAUNCH_LOCALES.length
  );

  const expectedMoney: Record<string, { currency: string; includes: string[] }> = {
    en: { currency: "USD", includes: ["$", "1,234.56"] },
    es: { currency: "EUR", includes: ["€", ","] },
    fr: { currency: "EUR", includes: ["€", ","] },
    de: { currency: "EUR", includes: ["€", "1.234,56"] },
    pt: { currency: "BRL", includes: ["R$", "1.234,56"] },
    it: { currency: "EUR", includes: ["€", ","] },
  };

  const expectedDateOrder: Record<string, "mdy" | "dmy"> = {
    en: "mdy",
    es: "dmy",
    fr: "dmy",
    de: "dmy",
    pt: "dmy",
    it: "dmy",
  };

  const mustDifferKeys = [
    "nav.dashboard",
    "tx.log",
    "cal.title",
    "person.household",
    "person.sideHustle",
    "pdf.section.yearGlance",
    "txModal.save",
  ];

  for (const code of LAUNCH_LOCALES) {
    const dict = translations[code] ?? {};
    const enDict = translations.en ?? {};
    const tag = UI_LOCALE_TAGS[code];
    const moneySpec = expectedMoney[code];
    const money = formatCurrencyAmount(amt, moneySpec.currency, tag, false);

    for (const needle of moneySpec.includes) {
      assert(
        results,
        s,
        `${code} money contains ${JSON.stringify(needle)}`,
        money.includes(needle),
        needle,
        money
      );
    }

    const numericDate = new Intl.DateTimeFormat(tag, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(sampleDate);
    const order = expectedDateOrder[code];
    if (order === "mdy") {
      assert(
        results,
        s,
        `${code} date is MM/DD/YYYY`,
        numericDate.startsWith("01") && numericDate.includes("15"),
        "01/…/15",
        numericDate
      );
    } else {
      assert(
        results,
        s,
        `${code} date is DD-first (not US MM/DD)`,
        numericDate.startsWith("15") || numericDate.startsWith("15."),
        "15-first",
        numericDate
      );
    }

    const monFirst = calendarStartsOnMonday(code);
    assert(
      results,
      s,
      `${code} calendar week start`,
      code === "en" ? !monFirst : monFirst,
      code === "en" ? "Sun" : "Mon",
      monFirst ? "Mon" : "Sun"
    );

    const days = weekdaysForLocale(code);
    assert(results, s, `${code} has 7 weekday labels`, days.length === 7, 7, days.length);
    if (code === "en") {
      assert(
        results,
        s,
        "en weekdays start Sun",
        days[0].toLowerCase().startsWith("sun"),
        "Sun…",
        days[0]
      );
    } else {
      // Mon-first: first label should not be Sunday in Latin locales
      assert(
        results,
        s,
        `${code} weekdays start Mon (not Sun)`,
        !days[0].toLowerCase().startsWith("sun") &&
          !days[0].toLowerCase().startsWith("dom"),
        "Mon-first",
        days.join("|")
      );
    }

    // Dictionary coverage + non-English for non-en launch locales
    const missing = Object.keys(enDict).filter((k) => !dict[k]);
    assert(
      results,
      s,
      `${code} dict covers English keys`,
      missing.length === 0,
      0,
      missing.length ? missing.slice(0, 6).join(",") : 0
    );

    if (code !== "en") {
      for (const key of mustDifferKeys) {
        assert(
          results,
          s,
          `${code} ${key} ≠ English`,
          Boolean(dict[key]) && dict[key] !== enDict[key],
          enDict[key],
          dict[key]
        );
      }
    }

    // Person presets via translator
    const t = (key: string) => dict[key] ?? key;
    assert(
      results,
      s,
      `${code} personLabel(Household) uses t()`,
      personLabel("Household", t) === t("person.household"),
      t("person.household"),
      personLabel("Household", t)
    );
    assert(
      results,
      s,
      `${code} custom person left as user data`,
      personLabel("Side Hustle Extra", t) === "Side Hustle Extra" ||
        personLabel("Custom Biz", t) === "Custom Biz",
      "Custom Biz",
      personLabel("Custom Biz", t)
    );
  }

  // uiEs parity (legacy check still useful)
  const uiKeys = Object.keys(uiEn);
  const missingEs = uiKeys.filter((k) => !uiEs[k]);
  assert(
    results,
    s,
    "uiEs covers all uiEn keys",
    missingEs.length === 0,
    0,
    missingEs.length ? missingEs.slice(0, 8).join(",") : 0
  );
}

// ── Recurring + CSV helpers ───────────────────────────
function testRecurringAndCsv(results: AssertionResult[]) {
  const s = "recurring-csv";

  // Inline minimal recurring math (same as lib/recurring nextOccurrenceDate)
  const addMonths = (iso: string, months: number) => {
    const d = new Date(iso + "T12:00:00");
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
  };
  assert(results, s, "Monthly next date Jan→Feb", addMonths("2026-01-15", 1) === "2026-02-15", "2026-02-15", addMonths("2026-01-15", 1));

  // Skipped txs excluded from dashboard
  const withSkip: Transaction[] = [
    ...DS_SINGLE_MONTH,
    {
      id: "skip1",
      date: "2026-05-16",
      type: "expense",
      amount: 999,
      categoryId: "cat-groc",
      accountId: "a1",
      person: "Household",
      skipped: true,
    },
  ];
  const filtered = withSkip.filter((t) => !t.skipped);
  const dash = computeDashboardSummary(filtered, FIXTURE_CATEGORIES);
  assertMoney(results, s, "Skipped expense ignored (out=2000)", 2000, dash.expense);

  // CSV parse smoke
  const sample = "Date,Amount,Description\n2026-05-01,42.50,Coffee";
  const lines = sample.split("\n");
  assert(results, s, "CSV has header + 1 row", lines.length === 2, 2, lines.length);
}

// ── Chart crop helper (PNG export domain) ─────────────
function testChartCrop(results: AssertionResult[]) {
  const s = "chart-export-crop";
  // Inline same logic as cropToPopulatedRange
  const rows = [
    { name: "Jan", income: 100, expense: 50 },
    { name: "Feb", income: 100, expense: 50 },
    { name: "Mar", income: 0, expense: 0 },
    { name: "Dec", income: 0, expense: 0 },
  ];
  let first = -1;
  let last = -1;
  rows.forEach((r, i) => {
    if (r.income > 0 || r.expense > 0) {
      if (first < 0) first = i;
      last = i;
    }
  });
  const cropped = rows.slice(first, last + 1);
  assert(results, s, "Crop keeps Jan–Feb only", cropped.length === 2, 2, cropped.length);
  assert(results, s, "Crop last label = Feb", cropped[1].name === "Feb", "Feb", cropped[1].name);
}

// ── Clear All Data scope (categories vs accounts) ─────
function testClearAllDataScope(results: AssertionResult[]) {
  const s = "clear-all-scope";
  // Same restore plan clearAllData() applies (pure — no IndexedDB in Node harness)
  const afterClear = planClearAllData("en");

  assert(
    results,
    s,
    "Categories match default set after clear",
    matchesDefaultCategories(afterClear.categories),
    true,
    afterClear.categories.length
  );
  assert(
    results,
    s,
    "Default category count = shipped defaults",
    afterClear.categories.length === DEFAULT_CATEGORIES.length,
    DEFAULT_CATEGORIES.length,
    afterClear.categories.length
  );
  assert(results, s, "Accounts empty after clear", afterClear.accounts.length === 0, 0, afterClear.accounts.length);
  assert(results, s, "Transactions emptied", afterClear.transactions.length === 0, 0, afterClear.transactions.length);
  assert(results, s, "Debts emptied", afterClear.debts.length === 0, 0, afterClear.debts.length);
  assert(results, s, "Sinking funds emptied", afterClear.sinkingFunds.length === 0, 0, afterClear.sinkingFunds.length);
  assert(results, s, "Net worth logs emptied", afterClear.netWorthLogs.length === 0, 0, afterClear.netWorthLogs.length);

  // Usable immediately: expense categories exist for Add Transaction dropdown
  const expenseCats = afterClear.categories.filter((c) => c.type === "expense");
  const incomeCats = afterClear.categories.filter((c) => c.type === "income");
  assert(results, s, "Expense categories available for Add Tx", expenseCats.length > 0, true, expenseCats.length);
  assert(results, s, "Income categories available for Add Tx", incomeCats.length > 0, true, incomeCats.length);
  assert(
    results,
    s,
    "Includes Groceries default",
    afterClear.categories.some((c) => c.id === "cat-groc"),
    true,
    afterClear.categories.map((c) => c.id).join(",")
  );

  const esPlan = planClearAllData("es");
  assert(
    results,
    s,
    "ES clear plan keeps default category ids",
    matchesDefaultCategories(esPlan.categories),
    true,
    esPlan.categories.map((c) => c.id).join(",")
  );

  // Net worth / debt empty-state math stays safe with zero accounts
  const assets = afterClear.accounts.reduce((sum, _a) => sum, 0);
  assertMoney(results, s, "Zero accounts → assets total 0", 0, assets);
}

/** Full internal suite */
export function runCalculationSuite(): SuiteReport {
  const results: AssertionResult[] = [];

  testEmpty(results);
  testSingleMonth(results);
  testNegativeMonth(results);
  testFullYear(results);
  testDebts(results);
  testSinkingFunds(results);
  testNetWorth(results);
  testCurrencyLocale(results);
  testLifecycle(results);
  testExportParity(results);
  testPdfReportPayload(results);
  testLaunchLocales(results);
  testRecurringAndCsv(results);
  testChartCrop(results);
  testClearAllDataScope(results);

  const passedCount = results.filter((r) => r.passed).length;
  const failedCount = results.length - passedCount;
  const passed = failedCount === 0;

  return {
    passed,
    total: results.length,
    passedCount,
    failedCount,
    results,
    customerMessage: passed
      ? "✅ All calculations verified"
      : "⚠️ Something looks off — please contact support",
  };
}

/** Lightweight customer-facing check used from Settings */
export function runCustomerVerification(): { ok: boolean; message: string } {
  const report = runCalculationSuite();
  return { ok: report.passed, message: report.customerMessage };
}
